// src/Messanger/AssignUserModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import "./AssignUserModal.css";

// Change this if your backend route is different.
const SAVE_ENDPOINT = (projectId) => `/projects/${projectId}/assign-users`;

const getId = (u) => (typeof u === "string" ? u : u?._id || u?.id || "");

const getProjName = (p) =>
  String(p?.projectName || p?.name || "Untitled Project").trim();

export default function AssignUserModal({
  project,
  onClose,
  onAssignSuccess,
  users = [],
}) {
  const [localUsers, setLocalUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  const authHeaders = {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  };

  // Pre-tick current members
  useEffect(() => {
    const current = Array.isArray(project?.assignedUsers)
      ? project.assignedUsers
      : [];
    setSelected(current.map(getId).filter(Boolean));
  }, [project?._id]); // eslint-disable-line

  // Load users if not passed in
  const allUsers = users?.length ? users : localUsers;

  useEffect(() => {
    if (users?.length) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get("/users", { headers: authHeaders });
        if (!mounted) return;
        const arr = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setLocalUsers(Array.isArray(arr) ? arr : []);
      } catch {
        if (mounted) setLocalUsers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = allUsers
      .map((u) => ({ ...u, _id: getId(u) }))
      .filter((u) => u._id)
      .filter((u) =>
        term
          ? `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(term)
          : true,
      );
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return list;
  }, [allUsers, search]);

  const toggle = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(
        SAVE_ENDPOINT(project._id),
        { assignedUsers: selected },
        { headers: { ...authHeaders, "Content-Type": "application/json" } },
      );
      if (onAssignSuccess) await onAssignSuccess();
      if (onClose) onClose();
    } catch (err) {
      const status = err?.response?.status;
      const url = err?.config?.url;
      const body =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message || "";
      alert(
        "❌ Failed to update members. " +
          [status && `HTTP ${status}`, url && `PUT ${url}`, body]
            .filter(Boolean)
            .join(" | "),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aum-overlay" onClick={onClose}>
      <div className="aum-card" onClick={(e) => e.stopPropagation()}>
        <div className="aum-header">
          <div>
            <div className="aum-eyebrow">Assign Users</div>
            <h3 className="aum-title">{getProjName(project)}</h3>
          </div>
          <button
            type="button"
            className="aum-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="aum-body">
          <input
            className="aum-search"
            type="text"
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="aum-count">{selected.length} selected</div>

          <div className="aum-list">
            {loading ? (
              <div className="aum-empty">Loading users…</div>
            ) : filtered.length === 0 ? (
              <div className="aum-empty">No users found.</div>
            ) : (
              filtered.map((u) => {
                const on = selected.includes(u._id);
                return (
                  <label
                    key={u._id}
                    className={`aum-row${on ? " aum-row--on" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(u._id)}
                    />
                    <div>
                      <div className="aum-name">{u.name || u.email}</div>
                      {u.role && <div className="aum-role">{u.role}</div>}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div className="aum-footer">
          <button
            type="button"
            className="aum-btn aum-btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="aum-btn aum-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}